import React, { useState, useEffect } from 'react';
import SharedCard   from './SharedCard';
import SharedInput  from './SharedInput';
import SharedButton from './SharedButton';
import SharedSelect from './SharedSelect';
import CalendarListView   from './CalendarListView';
import CalendarWeeklyView from './CalendarWeeklyView';

const CalendarComponent = () => {
  const [events,      setEvents]      = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [teachers,    setTeachers]    = useState([]);
  const [students,    setStudents]    = useState([]);

  const [availableSubdisciplines, setAvailableSubdisciplines] = useState([]);
  const [availableTeachers,       setAvailableTeachers]       = useState([]);
  const [availableLevels,         setAvailableLevels]         = useState([]);

  const [newEventDate,    setNewEventDate]    = useState('');
  const [newEventTime,    setNewEventTime]    = useState('');
  const [newEventSubdisc, setNewEventSubdisc] = useState('');
  const [newEventLevel,   setNewEventLevel]   = useState('');
  const [newEventTeacher, setNewEventTeacher] = useState('');
  const [newEventPeriod,  setNewEventPeriod]  = useState('once');
  const [editingEventId,  setEditingEventId]  = useState(null);

  const [selectedEventDetails, setSelectedEventDetails] = useState(null);
  const [calendarViewMode,     setCalendarViewMode]     = useState('list');
  const [msg,                  setMsg]                  = useState(null);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3000); };

  const periodicityOptions = [
    { value: 'once',       label: 'Única vez'    },
    { value: 'weekly',     label: 'Semanalmente' },
    { value: 'monthly',    label: 'Mensual'      },
    { value: 'semiannual', label: 'Semestral'    },
  ];

  // Carga inicial
  useEffect(() => {
    const load = async () => {
      try {
        const [evRes, prodRes, teachRes, stuRes] = await Promise.all([
          fetch('/api/list_events.php',   { credentials: 'include' }),
          fetch('/api/list_products.php', { credentials: 'include' }),
          fetch('/api/list_teachers.php', { credentials: 'include' }),
          fetch('/api/list_students.php', { credentials: 'include' }),
        ]);
        const [evData, prodData, teachData, stuData] = await Promise.all([
          evRes.json(), prodRes.json(), teachRes.json(), stuRes.json()
        ]);
        setEvents(     evData.success ? evData.events : []);
        setDisciplines(Array.isArray(prodData)  ? prodData.filter(p => p.type === 'discipline') : []);
        setTeachers(   Array.isArray(teachData) ? teachData : []);
        setStudents(   Array.isArray(stuData)   ? stuData   : []);
      } catch (err) {
        console.error('Calendar load error:', err);
      }
    };
    load();
  }, []);

  // Derivar opciones de selects
  useEffect(() => {
    const subs = [...new Set(disciplines.flatMap(d => d.subdisciplines || []).filter(Boolean))];
    setAvailableSubdisciplines(subs.map(s => ({ value: s, label: s })));
    setAvailableTeachers(teachers.map(t => ({ value: t.name, label: t.name })));

    // FIX: niveles desde enrollments de estudiantes (nueva estructura)
    const levels = [...new Set(
      students.flatMap(s => (s.enrollments || []).map(e => e.level)).filter(Boolean)
    )].sort();
    setAvailableLevels(levels.map(l => ({ value: l, label: l })));
  }, [disciplines, teachers, students]);

  const reloadEvents = async () => {
    try {
      const res  = await fetch('/api/list_events.php', { credentials: 'include' });
      const data = await res.json();
      setEvents(data.success ? data.events : []);
    } catch { setEvents([]); }
  };

  const handleAddOrUpdateEvent = async e => {
    e.preventDefault();
    if (!newEventDate || !newEventTime || !newEventSubdisc || !newEventLevel || !newEventTeacher) {
      showMsg('error', 'Completa todos los campos.');
      return;
    }
    const payload = {
      date:          newEventDate,
      time:          newEventTime,
      subdiscipline: newEventSubdisc,
      level:         newEventLevel,
      teacher:       newEventTeacher,
      periodicity:   newEventPeriod,
    };
    if (editingEventId) payload.id = editingEventId;

    try {
      const res  = await fetch('/api/register_event.php', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error al guardar');
      showMsg('success', editingEventId ? 'Evento actualizado.' : 'Evento creado.');
      await reloadEvents();
      resetForm();
    } catch (err) {
      showMsg('error', err.message);
    }
  };

  const handleEditClick = ev => {
    // FIX: no permitir editar eventos de horario de maestros
    if (ev.type === 'schedule') {
      showMsg('error', 'Los horarios de maestros se editan desde la página de Maestros.');
      return;
    }
    setEditingEventId(ev.id);
    setNewEventDate(ev.date);
    setNewEventTime(ev.time);
    setNewEventSubdisc(ev.subdiscipline);
    setNewEventLevel(ev.level);
    setNewEventTeacher(ev.teacher);
    setNewEventPeriod(ev.periodicity);
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const handleDeleteEvent = async id => {
    // Eventos de horario no se pueden borrar aquí
    if (String(id).startsWith('sched_')) {
      showMsg('error', 'Los horarios de maestros se gestionan desde la página de Maestros.');
      return;
    }
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      const res  = await fetch('/api/delete_event.php', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error al eliminar');
      showMsg('success', 'Evento eliminado.');
      await reloadEvents();
    } catch (err) {
      showMsg('error', err.message);
    }
  };

  const resetForm = () => {
    setEditingEventId(null);
    setNewEventDate('');
    setNewEventTime('');
    setNewEventSubdisc('');
    setNewEventLevel('');
    setNewEventTeacher('');
    setNewEventPeriod('once');
  };

  return (
    <SharedCard title="Calendario de Clases y Eventos">

      {msg && (
        <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
          msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>{msg.text}</div>
      )}

      {/* Selector de vista */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Eventos</h3>
        <SharedSelect
          value={calendarViewMode}
          onChange={e => setCalendarViewMode(e.target.value)}
          options={[
            { value: 'list',   label: 'Vista Lista'   },
            { value: 'weekly', label: 'Vista Semanal' },
          ]}
        />
      </div>

      {/* Vistas */}
      {calendarViewMode === 'list'
        ? <CalendarListView
            events={events}
            onEdit={handleEditClick}
            onDelete={handleDeleteEvent}
            onSelectEvent={setSelectedEventDetails}
          />
        : <CalendarWeeklyView
            events={events}
            onEdit={handleEditClick}
            onDelete={handleDeleteEvent}
            onSelectEvent={setSelectedEventDetails}
          />
      }

      {/* Modal detalles */}
      {selectedEventDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4">
            <h3 className="font-bold text-lg mb-4 text-gray-900">Detalles del evento</h3>
            <div className="space-y-2 text-sm text-gray-700 mb-4">
              <p><span className="font-semibold">Fecha:</span> {selectedEventDetails.date}</p>
              <p><span className="font-semibold">Hora:</span> {selectedEventDetails.time}</p>
              <p><span className="font-semibold">Subdisciplina:</span> {selectedEventDetails.subdiscipline}</p>
              <p><span className="font-semibold">Nivel:</span> {selectedEventDetails.level}</p>
              <p><span className="font-semibold">Maestro:</span> {selectedEventDetails.teacher}</p>
              <p><span className="font-semibold">Periodicidad:</span> {selectedEventDetails.periodicity}</p>
              {selectedEventDetails.type === 'schedule' && (
                <p className="text-xs text-blue-600 bg-blue-50 px-3 py-1 rounded-full inline-block">
                  Horario de clase — editar desde Maestros
                </p>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {selectedEventDetails.type !== 'schedule' && (
                <>
                  <SharedButton onClick={() => { handleEditClick(selectedEventDetails); setSelectedEventDetails(null); }}>
                    Editar
                  </SharedButton>
                  <SharedButton
                    onClick={() => { handleDeleteEvent(selectedEventDetails.id); setSelectedEventDetails(null); }}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Eliminar
                  </SharedButton>
                </>
              )}
              <SharedButton onClick={() => setSelectedEventDetails(null)} className="bg-gray-500 hover:bg-gray-600">
                Cerrar
              </SharedButton>
            </div>
          </div>
        </div>
      )}

      {/* Formulario */}
      <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 mb-5">
          {editingEventId ? '✏️ Editar evento' : '➕ Agregar evento'}
        </h3>
        <form onSubmit={handleAddOrUpdateEvent}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SharedInput
              label="Fecha"
              type="date"
              value={newEventDate}
              onChange={e => setNewEventDate(e.target.value)}
            />
            <SharedInput
              label="Hora"
              type="time"
              value={newEventTime}
              onChange={e => setNewEventTime(e.target.value)}
            />
            <SharedSelect
              label="Subdisciplina"
              value={newEventSubdisc}
              onChange={e => setNewEventSubdisc(e.target.value)}
              options={[{ value: '', label: 'Selecciona' }, ...availableSubdisciplines]}
            />
            <SharedSelect
              label="Nivel"
              value={newEventLevel}
              onChange={e => setNewEventLevel(e.target.value)}
              options={[{ value: '', label: 'Selecciona' }, ...availableLevels]}
            />
            <SharedSelect
              label="Maestro"
              value={newEventTeacher}
              onChange={e => setNewEventTeacher(e.target.value)}
              options={[{ value: '', label: 'Selecciona' }, ...availableTeachers]}
            />
            <SharedSelect
              label="Periodicidad"
              value={newEventPeriod}
              onChange={e => setNewEventPeriod(e.target.value)}
              options={periodicityOptions}
            />
          </div>
          <div className="flex gap-3 mt-5">
            <SharedButton type="submit">
              {editingEventId ? 'Actualizar evento' : 'Agregar evento'}
            </SharedButton>
            {editingEventId && (
              <SharedButton type="button" onClick={resetForm} className="bg-gray-500 hover:bg-gray-600">
                Cancelar
              </SharedButton>
            )}
          </div>
        </form>
      </div>
    </SharedCard>
  );
};

export default CalendarComponent;